/**
 * App frame: one <nav> that is a bottom tab bar on phones, a 72px icon rail on tablets and
 * a 240px sidebar on desktop (pure CSS), plus an offline notice.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { routes, useRouter } from '../state/router.tsx';
import { useAppData, useLoadedData } from '../state/app-data.tsx';
import { useReviewQueue } from '../state/selectors.ts';
import { Icon, type IconName } from './Icon.tsx';
import { RoboCat } from './RoboCat.tsx';
import { languageInfo } from './course.ts';
import './app-shell.css';

const TABS: readonly { to: string; label: string; desktopLabel?: string; icon: IconName }[] = [
  { to: routes.home, label: 'Главная', icon: 'home' },
  { to: routes.words, label: 'Слова', icon: 'cards' },
  { to: routes.chat, label: 'Репетитор', icon: 'chat' },
  { to: routes.league, label: 'Лига', icon: 'league' },
  { to: routes.profile, label: 'Профиль', icon: 'profile' },
];

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

export function AppShell({
  children,
  immersive,
  onOpenCourses,
}: {
  children: ReactNode;
  /** Lesson mode: no tab bar on phones. */
  immersive: boolean;
  onOpenCourses: () => void;
}) {
  const { path, navigate } = useRouter();
  const { stale } = useAppData();
  const { courses, activeCourseId } = useLoadedData();
  const [now] = useState(() => new Date());
  const dueCount = useReviewQueue(now).cards.length;
  const online = useOnline();
  const course = courses.find((c) => c.id === activeCourseId);
  const language = languageInfo(course?.lang);

  return (
    <div className={`shell${immersive ? ' shell--immersive' : ''}`}>
      <nav className="nav" aria-label="Разделы">
        <div className="nav__brand">Сөйле</div>
        <ul className="nav__list">
          {TABS.map((tab) => {
            const active = tab.to === routes.home ? path === '/' : path.startsWith(tab.to);
            return (
              <li key={tab.to}>
                <a
                  href={tab.to}
                  className={`nav__item${active ? ' nav__item--active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  aria-label={tab.label}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(tab.to);
                  }}
                >
                  <Icon name={tab.icon} />
                  <span className="nav__label nav__label--phone">{tab.label}</span>
                  <span className="nav__label nav__label--desktop">{tab.desktopLabel ?? tab.label}</span>
                  {tab.to === routes.words && dueCount > 0 && <span className="nav__badge">{dueCount}</span>}
                </a>
              </li>
            );
          })}
        </ul>
        {course && (
          <button type="button" className="nav__course" onClick={onOpenCourses}>
            <RoboCat size={40} />
            <span className="nav__course-text">
              <strong>{language.titleRu}</strong>
              <span className="t-meta">{course.title}</span>
            </span>
            <Icon name="chevronDown" size={18} />
          </button>
        )}
      </nav>
      <main className="shell__main">
        {(!online || stale) && (
          <p className="offline-note" role="status">
            <Icon name="offline" size={18} />
            {online ? 'Нет связи с сервером — показаны сохранённые данные.' : 'Нет интернета. Прогресс сохранится, когда связь вернётся.'}
          </p>
        )}
        {children}
      </main>
    </div>
  );
}
